import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useLocation, useParams } from "react-router";
import Attachments from "../../elements/common/attachments/Attachments";
import Comments from "../../elements/common/comments/Comments";
import TestCaseDetails from "./single-test-case/TestCaseDetails";
import TestCaseExecutionTable from "./single-test-case/TestCaseExecutionTable";
import TestScriptsContainer from "./single-test-case/test-scripts/TestScriptsContainer";
import TraceabilityComponent from "../../elements/common/traceability/TraceabilityComponent";
import TestCaseHistory from "./history/TestCaseHistory";
import { getTestCase } from "../../../modules/jira";
import {
  archiveViewPageTabs,
  singleViewPageTabs,
} from "../test-case/testCaseTableHead";
import SecondaryHeader from "../../elements/SecondaryHeader";
import { getAssociatedUsers } from "../../../modules/project";
import { useNotification } from "../../../hooks/NotificationContext";
import Loader from "../../elements/Loader";
import Error from "../../elements/Error";
import { Link } from "react-router-dom";

const entityType = "TestCase";
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const TestCaseViewPage = () => {
  const query = useQuery();
  const searchTerm = query.get("type");
  const [activeTab, setActiveTab] = useState(searchTerm || "details");
  const { testCaseKey, projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [testCaseId, setTestCaseId] = useState(null);
  const [error, setError] = useState(false);

  const { addNotification } = useNotification();

  const fetchTestCaseData = async () => {
    setLoading(true);
    try {
      const response = await getTestCase(testCaseKey, projectId);
      setTestCaseId(response?.id);
      setData(response);
    } catch (error) {
      if (error?.message === "NOT_FOUND") {
        setError("Test case with the selected key does not exist.");
      } else {
        const errorMessage =
          error?.message ||
          "Unable to fetch test case. Please try again later...";
        addNotification("danger", errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const getUsersFromJira = async () => {
    try {
      const usersResponse = await getAssociatedUsers(projectId);
      if (usersResponse) {
        setUsers(usersResponse?.map((user) => user?.user));
      }
    } catch (error) {
      addNotification("Error fetching Jira users:", error);
    }
  };

  useEffect(() => {
    fetchTestCaseData();
    getUsersFromJira();
  }, [testCaseKey]);

  if (error) {
    return (
      <Error>
        <h2>Test Case Not Found</h2>
        <p className="text-center">
          The test case you're looking for doesn't exist or may have been removed.{" "}
          <Link to={`/project/${projectId}/testcases`}>Click here{" "}</Link>to view
          all test cases
        </p>
      </Error>
    );
  }

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Container fluid>
          <SecondaryHeader
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            pageTitle={data?.summary}
            breadcrumbs={[
              {
                label: "Test Cases",
                path: `/project/${projectId}/testcases`,
                clickable: true,
              },
              {
                label: data?.key,
                path: `/project/${projectId}/testcases/${testCaseKey}`,
                clickable: false,
              },
            ]}
            tabHeaders={
              data?.is_archived ? archiveViewPageTabs : singleViewPageTabs
            }
            showBackButton={true}
          />
          <Container fluid className="px-0">
            <>
              {activeTab === "details" && (
                <TestCaseDetails
                  data={data}
                  testCaseId={testCaseId}
                  fetchTestCaseData={fetchTestCaseData}
                  projectId={projectId}
                />
              )}
              {activeTab === "testscripts" && (
                <TestScriptsContainer data={data} testCaseId={testCaseId} />
              )}
              {activeTab === "execution" && (
                <TestCaseExecutionTable
                  testCaseId={testCaseId}
                  users={users}
                  testCaseKey={data?.key}
                />
              )}
              {activeTab === "traceability" && (
                <TraceabilityComponent
                  entityId={testCaseId}
                  entityType={entityType}
                  entityKey={data?.key || testCaseKey}
                />
              )}
              {activeTab === "attachments" && (
                <Container fluid className="px-0">
                  <Attachments
                    users={users || []}
                    entityType={entityType}
                    entityId={testCaseId}
                  />
                </Container>
              )}
              {activeTab === "comments" && (
                <Container fluid className="px-0">
                  <Comments
                    entityType={entityType}
                    entityId={testCaseId}
                    users={users || []}
                    entityKey={data?.key}
                  />
                </Container>
              )}
              {activeTab === "history" && (
                <Container fluid className="px-0">
                  <TestCaseHistory
                    entityType={entityType}
                    entityId={testCaseId}
                    users={users || []}
                    projectId={projectId}
                  />
                </Container>
              )}
            </>
          </Container>
        </Container>
      )}
    </>
  );
};

export default TestCaseViewPage;
